import { isServer } from '@vue-storefront/core/helpers'

const PLACEHOLDER_EMAILS = new Set(['tbd@tbd.com'])
const PLACEHOLDER_VALUES = new Set(['tbd', 'tbd 1'])
const REPEATED_DIGIT_PATTERN = /^(\d)\1*$/

const COUNTRY_DIALING_CODES: { [countryId: string]: string } = {
  SE: '46',
  NO: '47',
  DK: '45',
  FI: '358'
}

const GTAG_SET_COMMAND = 'set'
const GTAG_USER_DATA_KEY = 'user_data'

const INTERNATIONAL_PREFIX = '+'
const INTERNATIONAL_ACCESS_CODE = '00'
const NATIONAL_TRUNK_PREFIX = '0'
const COUNTRY_CODE_LENGTH = 2
const MIN_NATIONAL_DIGITS = 7

const E164_PATTERN = /^\+[1-9]\d{7,14}$/
const NON_DIGIT_CHARACTERS = /\D/g
const CONSECUTIVE_WHITESPACE = /\s+/g
const ANY_WHITESPACE = /\s/g

export interface BillingAddressLike {
  email?: string,
  telephone?: string,
  firstname?: string,
  lastname?: string,
  street?: string | string[],
  city?: string,
  region?: string,
  postcode?: string,
  country_id?: string
}

/**
 * @see https://support.google.com/google-ads/answer/9888656
 */
export interface EnhancedConversionUserData {
  email?: string,
  phone_number?: string,
  address?: {
    first_name?: string,
    last_name?: string,
    street?: string,
    city?: string,
    region?: string,
    postal_code?: string,
    country?: string
  }
}

/**
 * @param {String} email
 * @return {String|null}
 */
const normalizeEmail = (email: string): string | null => {
  const normalized = email.trim().toLowerCase()
  if (!normalized || PLACEHOLDER_EMAILS.has(normalized)) {
    return null
  }
  return normalized
}

/**
 * @param {String} national
 * @return {String}
 */
const stripNationalTrunkPrefix = (national: string): string =>
  national.startsWith(NATIONAL_TRUNK_PREFIX)
    ? national.slice(NATIONAL_TRUNK_PREFIX.length)
    : national

/**
 * @param {String} digits
 * @param {String|null} countryDialingCode
 * @return {String|null}
 */
const resolveDialingCode = (digits: string, countryDialingCode: string | null): string | null => {
  if (countryDialingCode && digits.startsWith(countryDialingCode)) {
    return countryDialingCode
  }
  return Object.values(COUNTRY_DIALING_CODES).find(code => digits.startsWith(code)) || countryDialingCode
}

/**
 * @param {String} phone
 * @param {String|null} countryId
 * @return {String|null}
 */
const normalizePhone = (phone: string, countryId?: string | null): string | null => {
  let digits = phone.replace(NON_DIGIT_CHARACTERS, '')
  if (!digits || REPEATED_DIGIT_PATTERN.test(digits)) {
    return null
  }

  if (digits.startsWith(INTERNATIONAL_ACCESS_CODE)) {
    digits = digits.slice(INTERNATIONAL_ACCESS_CODE.length)
  }

  const countryDialingCode = countryId ? COUNTRY_DIALING_CODES[countryId.trim().toUpperCase()] || null : null
  const dialingCode = resolveDialingCode(digits, countryDialingCode)
  if (!dialingCode) {
    return null
  }

  let national = stripNationalTrunkPrefix(
    digits.startsWith(dialingCode) ? digits.slice(dialingCode.length) : digits
  )

  const withoutRepeatedCode = stripNationalTrunkPrefix(national.slice(dialingCode.length))
  if (national.startsWith(dialingCode) && withoutRepeatedCode.length >= MIN_NATIONAL_DIGITS) {
    national = withoutRepeatedCode
  }

  const e164 = `${INTERNATIONAL_PREFIX}${dialingCode}${national}`
  return E164_PATTERN.test(e164) ? e164 : null
}

/**
 * @param {String} name
 * @return {String|null}
 */
const normalizeName = (name: string): string | null => {
  const normalized = name.trim().toLowerCase().replace(CONSECUTIVE_WHITESPACE, ' ')
  if (!normalized || PLACEHOLDER_VALUES.has(normalized)) {
    return null
  }
  return normalized
}

/**
 * @param {String} value
 * @return {String|null}
 */
const normalizeSingleLine = (value: string): string | null => {
  const normalized = value.trim().replace(CONSECUTIVE_WHITESPACE, ' ')
  return normalized || null
}

/**
 * @param {String|String[]} street
 * @return {String|null}
 */
const normalizeStreet = (street: string | string[]): string | null => {
  const parts = Array.isArray(street) ? street : [street]
  const normalized = normalizeSingleLine(parts.filter(Boolean).join(' '))
  if (!normalized || PLACEHOLDER_VALUES.has(normalized.toLowerCase())) {
    return null
  }
  return normalized
}

/**
 * @param {String} postcode
 * @return {String|null}
 */
const normalizePostalCode = (postcode: string): string | null => {
  const normalized = postcode.trim().replace(ANY_WHITESPACE, '')
  return normalized || null
}

/**
 * @param {String} countryId
 * @return {String|null}
 */
const normalizeCountry = (countryId: string): string | null => {
  const normalized = countryId.trim().toUpperCase()
  return normalized.length === COUNTRY_CODE_LENGTH ? normalized : null
}

/**
 * @param {Object} target
 * @param {String} key
 * @param {String|null} value
 * @return {void}
 */
const assignIfPresent = (target: object, key: string, value: string | null): void => {
  if (value) {
    target[key] = value
  }
}

/**
 * @param {BillingAddressLike|null} address
 * @return {EnhancedConversionUserData|null}
 * @see https://support.google.com/google-ads/answer/9888656
 */
export const buildUserData = (
  address: BillingAddressLike | null | undefined
): EnhancedConversionUserData | null => {
  if (isServer || !address) {
    return null
  }

  const addressData: EnhancedConversionUserData['address'] = {}
  assignIfPresent(addressData, 'first_name', address.firstname ? normalizeName(address.firstname) : null)
  assignIfPresent(addressData, 'last_name', address.lastname ? normalizeName(address.lastname) : null)
  assignIfPresent(addressData, 'street', address.street ? normalizeStreet(address.street) : null)
  assignIfPresent(addressData, 'city', address.city ? normalizeSingleLine(address.city) : null)
  assignIfPresent(addressData, 'region', address.region ? normalizeSingleLine(address.region) : null)
  assignIfPresent(addressData, 'postal_code', address.postcode ? normalizePostalCode(address.postcode) : null)
  assignIfPresent(addressData, 'country', address.country_id ? normalizeCountry(address.country_id) : null)

  const userData: EnhancedConversionUserData = {}
  assignIfPresent(userData, 'email', address.email ? normalizeEmail(address.email) : null)
  assignIfPresent(userData, 'phone_number', address.telephone ? normalizePhone(address.telephone, address.country_id) : null)
  if (Object.keys(addressData).length > 0) {
    userData.address = addressData
  }

  return Object.keys(userData).length > 0 ? userData : null
}

/**
 * @param {EnhancedConversionUserData} userData
 * @return {void}
 * @see https://developers.google.com/tag-platform/tag-manager/server-side/send-data
 */
export const setUserProvidedData = (userData: EnhancedConversionUserData): void => {
  if (isServer || typeof window === 'undefined') {
    return
  }

  const target = window as any
  target.dataLayer = target.dataLayer || []

  const gtag = function (_command: string, _key: string, _value: EnhancedConversionUserData): void {
    target.dataLayer.push(arguments)
  }

  gtag(GTAG_SET_COMMAND, GTAG_USER_DATA_KEY, userData)
}

/**
 * @param {Object|null} order
 * @return {BillingAddressLike|null}
 */
export const resolveOrderAddress = (order: {
  addressInformation?: {
    billingAddress?: BillingAddressLike,
    shippingAddress?: BillingAddressLike
  }
} | null | undefined): BillingAddressLike | null => {
  if (!order?.addressInformation) {
    return null
  }
  return (
    order.addressInformation.billingAddress ||
    order.addressInformation.shippingAddress ||
    null
  )
}
